export interface Widget {
  type: string;
  component: string;
  operations: Record<string, {
    endpoint: string;
    method: string;
    requiredData: string[];
  }>;
  requiredAgents: string[];
}

export interface WidgetAction {
  actionType: string;
  label: string;
  priority: number;
  endpoint?: string;
  method?: string;
}

/**
 * What callers actually hold (BUG-96 root cause).
 *
 * `TaskWidgetMapping.widget` is `{ type, component }` — no `operations`. It is
 * a REFERENCE to a widget, not the widget definition; the definition with the
 * `operations` map lives in this class's registry, keyed by the same `type`.
 * `executeWidget` used to take a `Widget` and index `.operations` straight off
 * the argument, so every caller crashed with
 * "Cannot read properties of undefined (reading 'get-position')".
 *
 * Typing the parameter honestly is what stops that recurring: the compiler now
 * says the argument may not carry `operations`, so the lookup has to happen.
 */
export type WidgetRef = { type: string } & Partial<Widget>;

export class WidgetManager {
  private widgetRegistry = new Map<string, Widget>();

  constructor() {
    this.initializeWidgets();
  }

  private initializeWidgets() {
    // Cash Position Widget
    this.widgetRegistry.set('cash-position', {
      type: 'cash-position',
      component: 'CashPositionWidget',
      operations: {
        'get-position': {
          endpoint: '/api/functions/v1/cash-position',
          method: 'GET',
          requiredData: ['companyId']
        },
        'forecast': {
          endpoint: '/api/functions/v1/cash-forecast',
          method: 'POST',
          requiredData: ['companyId', 'timeframe']
        }
      },
      requiredAgents: ['Windrose', 'Tell Tale']
    });

    // Payment Acceleration Widget
    this.widgetRegistry.set('payment-acceleration', {
      type: 'payment-acceleration',
      component: 'PaymentAccelerationWidget',
      operations: {
        'find-opportunities': {
          endpoint: '/api/functions/v1/receivables/acceleration',
          method: 'GET',
          requiredData: ['companyId']
        },
        'accelerate': {
          endpoint: '/api/functions/v1/receivables/accelerate',
          method: 'POST',
          requiredData: ['invoiceId', 'discountRate']
        }
      },
      requiredAgents: ['Poniente', 'Full Send']
    });

    // Vendor Payments Widget
    this.widgetRegistry.set('vendor-payments', {
      type: 'vendor-payments',
      component: 'VendorPaymentWidget',
      operations: {
        'optimize': {
          endpoint: '/api/functions/v1/payables/optimization',
          method: 'POST',
          requiredData: ['companyId', 'cashFlow']
        },
        'schedule': {
          endpoint: '/api/functions/v1/payables/schedule',
          method: 'POST',
          requiredData: ['billIds', 'schedule']
        }
      },
      requiredAgents: ['Poniente', 'Windrose']
    });

    // Contract Qualification Widget
    this.widgetRegistry.set('contract-qualification', {
      type: 'contract-qualification',
      component: 'ContractQualificationWidget',
      operations: {
        'check-strength': {
          endpoint: '/api/functions/v1/financial-strength',
          method: 'GET',
          requiredData: ['companyId']
        },
        'qualify': {
          endpoint: '/api/functions/v1/contract-qualification',
          method: 'POST',
          requiredData: ['contractValue', 'duration']
        }
      },
      requiredAgents: ['Levante', 'Windrose']
    });

    // Cross Border Widget
    this.widgetRegistry.set('cross-border', {
      type: 'cross-border',
      component: 'CrossBorderWidget',
      operations: {
        'analyze': {
          endpoint: '/api/functions/v1/cross-border',
          method: 'GET',
          requiredData: ['companyId']
        },
        'optimize': {
          endpoint: '/api/functions/v1/fx-optimization',
          method: 'POST',
          requiredData: ['currencies', 'exposures']
        }
      },
      requiredAgents: ['Hooked In', 'Poniente']
    });
  }

  /**
   * Run a widget's primary action against its real endpoint.
   *
   * Returns the endpoint's payload, or `null` when the widget cannot be
   * executed for ANY reason — unknown widget, unknown operation, a required
   * input nothing can supply, or a failed request.
   *
   * `null` rather than a throw is deliberate (BUG-96). This method used to
   * throw `Failed to execute widget: ${error.message}`, and the raw text
   * "Cannot read properties of undefined (reading 'get-position')" reached the
   * user through the caller's error path. "Cannot answer this" is an ordinary
   * outcome here, not an exception: the caller turns `null` into a human
   * message that says what the assistant CAN do. Nothing internal is
   * user-facing, and the reason is logged for whoever is debugging.
   *
   * NOTE (BUG-108): this used to take an `agentData` argument sourced from
   * `AgentOrchestrator.orchestrate()`. That module was 306 lines of mock
   * adapters returning a hardcoded `currentBalance: 2400000` with an invented
   * named-account breakdown, and it has been deleted. Nothing on this path
   * fabricates a figure any more, so this method no longer needs the argument
   * — but that also means fields which only the mock could supply (`cashFlow`,
   * `invoiceId`, …) are now genuinely unavailable, and asking for one is a
   * `null`, never a guess.
   */
  async executeWidget(widget: WidgetRef, actions: WidgetAction[]): Promise<Record<string, any> | null> {
    const definition = this.resolveDefinition(widget);
    if (!definition) {
      console.warn(`[WidgetManager] no widget definition for type "${widget?.type}"`);
      return null;
    }

    const primaryAction = actions?.[0];
    if (!primaryAction) {
      console.warn(`[WidgetManager] widget "${definition.type}" invoked with no actions`);
      return null;
    }

    const operation = definition.operations[primaryAction.actionType];
    if (!operation) {
      console.warn(
        `[WidgetManager] operation "${primaryAction.actionType}" not found for widget "${definition.type}"`,
      );
      return null;
    }

    const requestData = this.prepareRequestData(operation.requiredData);
    if (!requestData) {
      console.warn(
        `[WidgetManager] cannot supply required inputs [${operation.requiredData.join(', ')}] ` +
          `for "${definition.type}.${primaryAction.actionType}"`,
      );
      return null;
    }

    try {
      const response = await fetch(operation.endpoint, {
        method: operation.method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.getAuthToken()}`,
        },
        body: operation.method !== 'GET' ? JSON.stringify(requestData) : undefined,
      });

      if (!response.ok) {
        console.warn(`[WidgetManager] ${operation.endpoint} responded ${response.status}`);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`[WidgetManager] ${operation.endpoint} failed:`, error);
      return null;
    }
  }

  /**
   * The BUG-96 fix. Callers hold a `{ type, component }` reference; the
   * definition carrying `operations` lives in the registry under the same
   * `type`. Look it up rather than indexing `.operations` off the argument.
   * A caller that already holds a full definition is honoured as-is.
   */
  private resolveDefinition(widget: WidgetRef): Widget | undefined {
    if (!widget?.type) return undefined;
    const registered = this.widgetRegistry.get(widget.type);
    if (registered) return registered;
    return widget.operations ? (widget as Widget) : undefined;
  }

  /**
   * Build the request body, or `null` if any required field cannot be supplied.
   *
   * Every field is either derived from real context or refused. The previous
   * version fell back to `{}` for `cashFlow` and scavenged unknown fields out
   * of the mock orchestrator's responses, so a request went out carrying
   * invented inputs. A field we cannot supply now stops the call.
   */
  private prepareRequestData(requiredData: string[]): Record<string, any> | null {
    const requestData: Record<string, any> = {};

    for (const field of requiredData) {
      switch (field) {
        case 'companyId': {
          const companyId = this.getCompanyId();
          if (!companyId) return null;
          requestData.companyId = companyId;
          break;
        }
        case 'timeframe':
          requestData.timeframe = '30d';
          break;
        default:
          // `cashFlow`, `invoiceId`, `discountRate`, `contractValue`,
          // `duration`, `currencies`, `exposures`, `billIds`, `schedule` —
          // these are action parameters the assistant does not collect. There
          // is no honest value to send, so the widget does not run.
          return null;
      }
    }

    return requestData;
  }

  private getAuthToken(): string {
    return localStorage.getItem('auth_token') || '';
  }

  /**
   * The tenant this request belongs to, or `null` if unknown.
   *
   * Never falls back to a placeholder. The previous default was the literal
   * string `'default'`, which would have sent every tenant's request under the
   * same id — the wrong-tenant defect class this codebase has already paid for
   * twice (BUG-98, BUG-91). No company id means no request.
   */
  private getCompanyId(): string | null {
    return localStorage.getItem('company_id') || null;
  }

  getWidget(type: string): Widget | undefined {
    return this.widgetRegistry.get(type);
  }

  getAllWidgets(): Widget[] {
    return Array.from(this.widgetRegistry.values());
  }
}